import React, { useMemo } from 'react';
import { useDeliveryHistory } from '../hooks/useDeliveryHistory';
import { DeliveryHistory } from '../types/deliveryHistory.types';
import { useTableSort } from '@/shared/hooks/useTableSort';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import DateRangePicker from '@/shared/components/forms/DateRangePicker';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import '@/shared/styles/search-form.css';
import '../styles/DeliveryHistoryPage.css';

// 컬럼 정의
const COLUMNS = [
    { key: 'suppName', label: '협력사' },
    { key: 'riderName', label: '이름' },
    { key: 'deliveryStatusName', label: '운행상태' },
    { key: 'finishCnt', label: '완료' },
    { key: 'rejectCnt', label: '거절' },
    { key: 'assingCancelCnt', label: '배차취소' },
    { key: 'deliCancelCnt', label: '배달취소' },
    { key: 'morTimeCnt', label: '아침점심피크' },
    { key: 'afterNonCnt', label: '오후논피크' },
    { key: 'nightNonCnt', label: '저녁피크시간' },
    { key: 'nightCnt', label: '심야논피크' },
    { key: 'riderId', label: '아이디' },
    { key: 'phoneNum', label: '휴대폰번호' },
] as const;

const DeliveryHistoryPage: React.FC = () => {
    const {
        deliveryHistoryData,
        loading,
        error,
        filter,
        searchedFilter,
        page,
        limit,
        totals,
        pageTotals,
        partners,
        partnersLoading,
        statusCodes,
        statusLoading,
        userType,
        handleFilterChange,
        handleSearch,
        handleReset,
        handlePageChange,
        renderOperationStatus,
    } = useDeliveryHistory();

    // ========================================
    // 정렬 훅 사용
    // ========================================
    const { sortConfig, sortedData, handleSort } = useTableSort<DeliveryHistory>({
        data: deliveryHistoryData?.data || [],
        numericColumns: [
            'finishCnt',
            'rejectCnt',
            'assingCancelCnt',
            'deliCancelCnt',
            'morTimeCnt',
            'afterNonCnt',
            'nightNonCnt',
            'nightCnt',
        ],
    });

    // 협력사 옵션을 SelectOption 형식으로 변환
    const partnerOptions: SelectOption[] = useMemo(() => {
        if (!partners || partners.length === 0) return [];
        return partners.map((partner) => ({
            value: partner.suppId,
            label: partner.suppName || partner.suppId,
        }));
    }, [partners]);

    // 상태 옵션을 SelectOption 형식으로 변환
    const statusOptions: SelectOption[] = useMemo(() => {
        if (!statusCodes || statusCodes.length === 0) return [];
        return statusCodes.map((status) => ({
            value: status.code,
            label: status.name,
        }));
    }, [statusCodes]);

    const handleRowClick = (row: DeliveryHistory) => {
        try {
            // searchedFilter가 있으면 검색 실행된 필터 사용, 없으면 날짜 전달 안 함
            const dateFilter = searchedFilter || null;
            const payload: {
                suppId: string;
                suppName: string;
                riderId: string;
                riderName: string;
                startDate?: string;
                endDate?: string;
            } = {
                suppId: row.supplyId ? String(row.supplyId) : '',
                suppName: row.suppName || '',
                riderId: row.riderId,
                riderName: row.riderName || '',
            };

            // searchedFilter가 있을 때만 날짜 추가
            if (dateFilter) {
                payload.startDate = dateFilter.startDate || '';
                payload.endDate = dateFilter.endDate || '';
            }

            sessionStorage.setItem('deliveryHistoryDetailPrefill', JSON.stringify(payload));
            sessionStorage.setItem('deliveryHistoryDetailPrefillSource', 'history');
        } catch (error) {
            console.warn('Failed to store delivery history detail prefill:', error);
        }

        const navigateEvent = new CustomEvent('dashboard:navigate', {
            detail: { page: 'delivery_history_detail' },
        });
        window.dispatchEvent(navigateEvent);
    };

    if (loading) {
        return (
            <div className="delivery-history-page">
                <Loading />
            </div>
        );
    }

    if (error) {
        return (
            <div className="delivery-history-page">
                <div className="empty-state">
                    <h3>오류가 발생했습니다</h3>
                    <p>배달 내역을 불러오는 중 오류가 발생했습니다. 다시 시도해주세요.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="delivery-history-page">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">배달 내역 조회</h1>
            </div>

            <div className="card search-card">
                <div className="card-body">
                    <div className={`search-form ${userType === 'HEADQUARTERS' ? '' : 'supplier-view'}`}>
                        {/* 날짜 범위 선택 */}
                        <div className="form-group date-range-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-calendar-alt" />
                                    조회 기간
                                </label>
                                <DateRangePicker
                                    startDate={filter.startDate}
                                    endDate={filter.endDate}
                                    onStartDateChange={(value) => handleFilterChange({ startDate: value })}
                                    onEndDateChange={(value) => handleFilterChange({ endDate: value })}
                                    startLabel="시작일"
                                    endLabel="종료일"
                                />
                            </div>
                        </div>

                        {/* 본사 사용자만 협력사 선택 필드 표시 */}
                        {userType === 'HEADQUARTERS' && (
                            <div className="form-group">
                                <div className="form-field">
                                    <label className="form-label">
                                        <i className="fas fa-building" />
                                        협력사
                                    </label>
                                    <SearchableSelect
                                        options={partnerOptions}
                                        value={filter.partnerId}
                                        onChange={(value) => handleFilterChange({ partnerId: value })}
                                        placeholder="협력사 검색..."
                                        disabled={partnersLoading}
                                        showAllOption={true}
                                        allOptionLabel="전체"
                                    />
                                </div>
                            </div>
                        )}

                        {/* 상태 선택 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-flag" />
                                    상태
                                </label>
                                <SearchableSelect
                                    options={statusOptions}
                                    value={filter.status}
                                    onChange={(value) => handleFilterChange({ status: value })}
                                    placeholder="상태 선택..."
                                    disabled={statusLoading}
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>

                        {/* 버튼 그룹 - 오른쪽 정렬 */}
                        <div className="form-group button-group">
                            <button className="btn btn-secondary" onClick={handleReset}>
                                <i className="fas fa-redo" />
                                초기화
                            </button>
                            <button className="btn btn-primary" onClick={handleSearch}>
                                <i className="fas fa-search" />
                                검색
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h3 className="font-semibold">배달 내역</h3>
                </div>
                <div className="card-body">
                    {/* 검색 결과 건수 */}
                    {deliveryHistoryData?.data && deliveryHistoryData.data.length > 0 && (
                        <div className="result-count">
                            <i className="fas fa-list"></i>
                            검색 결과: <strong>{deliveryHistoryData.total.toLocaleString()}건</strong>
                        </div>
                    )}

                    {deliveryHistoryData?.data && deliveryHistoryData.data.length > 0 ? (
                        <>
                            <div className="table-responsive">
                                <table className="delivery-table sortable-table">
                                    <thead>
                                        <tr>
                                            {COLUMNS.map((col) => {
                                                const isSorted = sortConfig.columnKey === col.key;
                                                const direction = isSorted ? sortConfig.direction : null;
                                                return (
                                                    <th
                                                        key={col.key}
                                                        className={`sortable-header ${isSorted ? 'sorted' : ''}`}
                                                        onClick={() => handleSort(col.key)}
                                                    >
                                                        {col.label}
                                                        <SortIcon direction={direction} />
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="total-row">
                                            <td></td>
                                            <td>총 합계</td>
                                            <td>-</td>
                                            <td>{totals.finishCnt}</td>
                                            <td>{totals.rejectCnt}</td>
                                            <td>{totals.assingCancelCnt}</td>
                                            <td>{totals.deliCancelCnt}</td>
                                            <td>{totals.morTimeCnt}</td>
                                            <td>{totals.afterNonCnt}</td>
                                            <td>{totals.nightNonCnt}</td>
                                            <td>{totals.nightCnt}</td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                        <tr className="page-total-row">
                                            <td></td>
                                            <td>페이지별 합계</td>
                                            <td>-</td>
                                            <td>{pageTotals.finishCnt}</td>
                                            <td>{pageTotals.rejectCnt}</td>
                                            <td>{pageTotals.assingCancelCnt}</td>
                                            <td>{pageTotals.deliCancelCnt}</td>
                                            <td>{pageTotals.morTimeCnt}</td>
                                            <td>{pageTotals.afterNonCnt}</td>
                                            <td>{pageTotals.nightNonCnt}</td>
                                            <td>{pageTotals.nightCnt}</td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                        {sortedData.map((row: DeliveryHistory, idx: number) => (
                                            <tr
                                                key={`${row.supplyId}-${row.riderId}-${idx}`}
                                                className="clickable-row"
                                                onClick={() => handleRowClick(row)}
                                            >
                                                <td>{row.suppName}</td>
                                                <td>{row.riderName}</td>
                                                <td>{renderOperationStatus(row.deliveryStatusName)}</td>
                                                <td>{row.finishCnt}</td>
                                                <td>{row.rejectCnt}</td>
                                                <td>{row.assingCancelCnt}</td>
                                                <td>{row.deliCancelCnt}</td>
                                                <td>{row.morTimeCnt}</td>
                                                <td>{row.afterNonCnt}</td>
                                                <td>{row.nightNonCnt}</td>
                                                <td>{row.nightCnt}</td>
                                                <td>{row.riderId}</td>
                                                <td>{row.phoneNum ?? '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {deliveryHistoryData && deliveryHistoryData.total > limit && (
                                <div className="pagination-wrapper">
                                    <Pagination
                                        currentPage={page}
                                        totalPages={Math.ceil((deliveryHistoryData?.total || 0) / limit)}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="empty-state">
                            <h3>배달 내역이 없습니다</h3>
                            <p>검색 조건에 맞는 배달 내역이 없습니다.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeliveryHistoryPage;
